import { API_ENDPOINTS } from '../config';
import { ensureSignupFunnelId, getSignupFunnelId } from '../marketingTag';

const SESSION_KEY = 'gwc_journey_session_id';
const ANON_ID_KEY = 'gwc_anonymous_id';
const FIRST_TOUCH_KEY = 'gwc_journey_first_touch';
const DEDUPE_WINDOW_MS = 4000;
const recentEventMap = new Map();

const ALLOWED_EVENTS = new Set([
  'journey_started',
  'journey_page_view',
  'signup_click',
  'demo_query_submitted',
  'demo_results_shown',
  'demo_query_error',
  'demo_result_modal_open',
  'demo_result_cta_click',
  'register_view',
  'register_plan_selected',
  'register_submit',
  'register_success',
  'register_error',
  'compare_hub_card_click',
  'compare_cta_click',
  'compare_ai_prompt_clicked',
  'compare_hub_navigation',
  'prompt_assist_opened',
  'prompt_assist_prompt_clicked',
  'prompt_assist_upgrade_intent',
  'assistant_suggestion_clicked',
  'recommendation_results_shown',
  'recommendation_card_clicked',
  'recommendation_view_all_clicked',
  'recommendation_view_details_clicked',
  'recommendation_create_grant_clicked',
  'recommendation_source_website_clicked'
]);

function createId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = createId('sess');
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

function getAnonymousId() {
  let anonymousId = localStorage.getItem(ANON_ID_KEY);
  if (!anonymousId) {
    anonymousId = createId('anon');
    localStorage.setItem(ANON_ID_KEY, anonymousId);
  }
  return anonymousId;
}

function getOrCreateFirstTouch() {
  const existing = localStorage.getItem(FIRST_TOUCH_KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch (_err) {
      // Ignore malformed local data and re-create first touch context.
    }
  }

  const params = new URLSearchParams(window.location.search);
  const firstTouch = {
    source: params.get('utm_source') || 'direct',
    medium: params.get('utm_medium') || 'none',
    campaign: params.get('utm_campaign') || 'none',
    firstPath: window.location.pathname,
    firstSeenAt: new Date().toISOString()
  };

  localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  return firstTouch;
}

function buildEventFingerprint(eventName, dimensions = {}) {
  const selected = {
    path: window.location.pathname,
    source: dimensions.source,
    account_type: dimensions.account_type,
    error_type: dimensions.error_type
  };

  return `${eventName}|${JSON.stringify(selected)}`;
}

function shouldSuppressEvent(eventName, dimensions = {}) {
  const fingerprint = buildEventFingerprint(eventName, dimensions);
  const now = Date.now();
  const lastSentAt = recentEventMap.get(fingerprint);

  if (lastSentAt && now - lastSentAt < DEDUPE_WINDOW_MS) {
    return true;
  }

  recentEventMap.set(fingerprint, now);

  if (recentEventMap.size > 100) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [key, timestamp] of recentEventMap.entries()) {
      if (timestamp < cutoff) {
        recentEventMap.delete(key);
      }
    }
  }

  return false;
}

function sanitizeDimensions(dimensions = {}) {
  const sanitized = {};
  Object.entries(dimensions).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      sanitized[key] = value.slice(0, 500);
      return;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
      return;
    }

    sanitized[key] = JSON.stringify(value).slice(0, 500);
  });
  return sanitized;
}

export async function trackJourneyTelemetry(eventName, dimensions = {}, options = {}) {
  if (!ALLOWED_EVENTS.has(eventName)) {
    return;
  }

  if (shouldSuppressEvent(eventName, dimensions)) {
    return;
  }

  const firstTouch = getOrCreateFirstTouch();
  const funnelId = getSignupFunnelId() || ensureSignupFunnelId();

  const payload = {
    eventName,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    funnelId,
    anonymousId: getAnonymousId(),
    registeredUserId: options.registeredUserId || undefined,
    path: window.location.pathname,
    hostname: window.location.hostname,
    referrer: document.referrer || undefined,
    utmSource: firstTouch.source,
    utmMedium: firstTouch.medium,
    utmCampaign: firstTouch.campaign,
    dimensions: sanitizeDimensions(dimensions)
  };

  try {
    await fetch(`${API_ENDPOINTS.api}/journey/telemetry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      keepalive: true
    });
  } catch (error) {
    console.warn('[JourneyTelemetry] Failed to send event', eventName, error);
  }
}
